import * as React from "react";
import { Box, Card, Chip, Divider, Grid, Stack, Typography } from "@mui/material";
import { RobotIcon } from "@phosphor-icons/react/dist/ssr/Robot";

import type { CampaignRequestAgent, CampaignRequestDetail } from "@/hooks/api/use-campaign-requests";

import { DocumentList } from "../../components";
import { agentKindNeedsCallFlow, marketFlag, marketLabel } from "../../constants";
import { agentHeading, agentKindTag, agentLanguageLine } from "../../view/agent-naming";
import { agentSource, EmptyPanel, isEnglish, MissingLine } from "./parts";

/**
 * Agents: what will answer the phone, and what it was built from.
 *
 * This was a six-column table for three rows. Two of those columns held the same value — an
 * agent's nickname defaults to its language, so Agent said "English" and Language said "English"
 * — one column was em-dashes on every row for a feature that does not exist, and the Status badge
 * repeated the Source cell word for word. Three rows is a card set, so it is one.
 */
export function AgentsTab({ request }: { request: CampaignRequestDetail }): React.JSX.Element {
	const agents = request.agents;
	const nonEnglish = agents.filter((agent) => !isEnglish(agent));

	return (
		<Stack spacing={3}>
			{agents.length === 0 ? (
				<EmptyPanel icon={<RobotIcon size={28} />} title="No agents on this request">
					Provisioning will still build the campaigns — they will have nothing behind them to answer.
				</EmptyPanel>
			) : (
				<Grid container spacing={2}>
					{agents.map((agent) => (
						<Grid key={agent.id} size={{ lg: 4, md: 6, xs: 12 }}>
							<AgentCard agent={agent} documents={request.documents} />
						</Grid>
					))}
				</Grid>
			)}

			{/*
			 * One line where a full-width amber panel used to argue the case at three sentences. The
			 * fact worth carrying is which agents trigger the extra pipeline step; why it matters is
			 * a thing the pipeline owner knows and the person provisioning does not act on.
			 */}
			{nonEnglish.length > 0 ? (
				<Typography variant="body2" color="text.secondary">
					{`${nonEnglish.map((agent) => agentHeading(agent)).join(" and ")} ${
						nonEnglish.length === 1 ? "is" : "are"
					} analysed from an English translation of the call audio and transcript.`}
				</Typography>
			) : null}

			{agents.length > 0 ? <MissingLine>Review and adjust each draft on Processing & Approval.</MissingLine> : null}

			<Divider />

			<Box>
				<DocumentList
					requestId={request.id}
					documents={request.documents}
					filterKinds={["call_flow", "revised_call_flow"]}
					kinds={["revised_call_flow"]}
					title="Call flow"
					emptyText="No call flow attached — a new or revised agent cannot be built without one."
					allowDelete={false}
				/>
				{/* Placed where the generated prompt would have been: directly under the flow it
				    would have been generated from. */}
				<Box sx={{ pt: 1.5 }}>
					<MissingLine>Generated prompts are available on Processing & Approval after processing finishes.</MissingLine>
				</Box>
			</Box>
		</Stack>
	);
}

/**
 * One agent.
 *
 * Two chips and no more. The kind is a tag — what sort of build this is — and the status is a
 * state: whether anything is still outstanding. They never say the same thing, and neither repeats
 * the line of text beside it: when a flow is attached the filename is the evidence and the chip
 * does not restate it, and when one is missing there is no filename to print so the chip carries
 * it alone.
 */
function AgentCard({
	agent,
	documents,
}: {
	agent: CampaignRequestAgent;
	documents: CampaignRequestDetail["documents"];
}): React.JSX.Element {
	const awaitingFlow = agentKindNeedsCallFlow(agent.kind) && !agent.callFlowDocumentId;
	const aside = agentLanguageLine(agent);
	const kind = agentKindTag(agent.kind);
	const source = awaitingFlow ? null : agentSource(agent, documents);

	return (
		<Card variant="outlined" sx={{ height: "100%", p: 2 }}>
			<Stack spacing={1} sx={{ height: "100%" }}>
				<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
					<Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
						<Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
							{agentHeading(agent)}
						</Typography>
						{aside ? (
							<Typography variant="caption" color="text.secondary">
								{aside}
							</Typography>
						) : null}
					</Box>

					<Chip
						size="small"
						variant="soft"
						color={awaitingFlow ? "warning" : "success"}
						label={awaitingFlow ? "Awaiting flow" : "Ready"}
					/>
				</Stack>

				{kind ? (
					<Box>
						<Chip size="small" variant="outlined" label={kind} />
					</Box>
				) : null}

				{/* Flag and name as separate spans with a real gap: a flag emoji carries no side
				    bearing, so `${flag} ${label}` in one string renders them touching. */}
				{agent.markets.length > 0 ? (
					<Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap" }}>
						{agent.markets.map((code) => (
							<Stack key={code} direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
								<Box component="span">{marketFlag(code)}</Box>
								<Typography variant="body2">{marketLabel(code)}</Typography>
							</Stack>
						))}
					</Stack>
				) : (
					<Typography variant="body2" color="text.secondary">
						No market bound
					</Typography>
				)}

				{source ? (
					<Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-word" }}>
						{source}
					</Typography>
				) : null}
			</Stack>
		</Card>
	);
}
